import { ApiClient } from '@/services/api'
import type { Incident, Route } from '@/types'

const throwIfFieldSupervisorAuthorityError = (error: any) => {
  const status = error?.response?.status || error?.status
  const code = error?.response?.data?.code || error?.code

  if (status === 403 || code === 'FIELD_SUPERVISOR_SCOPE_DENIED') {
    const normalized = new Error(error?.response?.data?.message || 'Acceso denegado para el alcance del supervisor de campo')
    ;(normalized as any).status = 403
    ;(normalized as any).code = 'FIELD_SUPERVISOR_SCOPE_DENIED'
    throw normalized
  }

  if (status === 501 || code === 'NOT_IMPLEMENTED_YET') {
    const normalized = new Error(error?.response?.data?.message || 'Funcionalidad pendiente de implementación backend con alcance real')
    ;(normalized as any).status = 501
    ;(normalized as any).code = 'NOT_IMPLEMENTED_YET'
    throw normalized
  }
}

/**
 * P13 W2 · XSC-M1 — the CANONICAL supervised group/agency corridor of the signed FS intake.
 *
 * `GET /api/groups/with-agencies` is the server-scoped reader (`getGroupsWithAgencies`, bounded by
 * `resolveDashboardScope` → `buildGroupScopeWhere`) whose `field_supervisor` partition is derived
 * from the SAME canonical resolver the intake POST uses. Every option below carries the agency's
 * own `groupId` (the Point row's foreign key), cross-checked against its containing group — the
 * binding `POST /api/field-supervisor/incidents` re-proves server-side.
 */
export interface SupervisedGroupOption {
  groupId: number
  groupName: string
}

export interface SupervisedAgencyOption {
  agencyId: number
  agencyName: string
  address: string | null
  /** Canonical owning group — the agency row's own `groupId`, equal to its containing group. */
  groupId: number
  groupName: string
}

export interface SupervisedGroupsWithAgencies {
  groups: SupervisedGroupOption[]
  agencies: SupervisedAgencyOption[]
}

/**
 * P13-V7B (refutation) — the largest page `GET /field-supervisor/incidents` serves: the backend clamps
 * `?limit` to 1..200 (field-supervisor.js, `fsListLimit`) and states no total of its own.
 */
export const FS_ZONE_INCIDENTS_PAGE_LIMIT = 200

/**
 * P13-V7B (refutation) — the zone counts the incidents page may publish. Every count is `null` unless
 * `complete`: a full page means the server returned its maximum and did not say how many exist.
 */
export interface FieldSupervisorZoneIncidentStats {
  complete: boolean
  pageLimit: number
  zoneIncidents: number | null
  openIncidents: number | null
  closedIncidents: number | null
  reportedByOthers: number | null
}

const positiveInt = (value: unknown): number | null => {
  const n = Number(value)
  return Number.isInteger(n) && n > 0 ? n : null
}

/**
 * Servicios específicos para el perfil Supervisor de Campo
 * Maneja operaciones filtradas por grupos asignados
 */
export class SupervisorService {

  /**
   * P13 W2 · XSC-M1 — supervised groups and agencies for the signed intake selector.
   *
   * THE CLIENT CONTRACT, stated once. The canonical response interceptor (`api-client.ts`) has
   * ALREADY unwrapped `{ success, data }` before this code runs, and the `@/services/api` facade
   * re-wraps that unwrapped value as `response.data`. So `response.data` IS the groups array.
   * Reading `.data` again (`response.data?.data`) would read a property of an array — `undefined` —
   * and a `?? []` would turn that into a silently EMPTY selector, which is exactly the shape defect
   * that made the previous positive control of this journey vacuous. An impossible shape is
   * therefore a NAMED failure, never an empty list.
   *
   * No fallback exists: not to `/field-supervisor/assigned-agencies` (contained to vendor-only by
   * #214), not to `/points`, not to the group directory. The server partition is the only source.
   */
  static async getSupervisedGroupsWithAgencies(): Promise<SupervisedGroupsWithAgencies> {
    const response = await ApiClient.get('/groups/with-agencies')
    const groups = response.data
    if (!Array.isArray(groups)) {
      throw new Error(`SUPERVISED_GROUPS_SHAPE_INVALID: expected an array after the canonical unwrap, received ${groups === null ? 'null' : typeof groups}`)
    }

    const groupOptions: SupervisedGroupOption[] = []
    const agencyOptions: SupervisedAgencyOption[] = []
    for (const group of groups) {
      const groupId = positiveInt(group?.id)
      const groupName = typeof group?.name === 'string' && group.name.trim() ? group.name : null
      if (!groupId || !groupName) {
        throw new Error('SUPERVISED_GROUP_ROW_INVALID: a group without a positive id and a name cannot own an agency')
      }
      if (!Array.isArray(group.agencies)) {
        throw new Error(`SUPERVISED_GROUP_ROW_INVALID: group ${groupId} carries no agencies array`)
      }
      groupOptions.push({ groupId, groupName })

      for (const agency of group.agencies) {
        const agencyId = positiveInt(agency?.id)
        const agencyGroupId = positiveInt(agency?.groupId)
        const agencyName = typeof agency?.name === 'string' && agency.name.trim() ? agency.name : null
        if (!agencyId || !agencyName) {
          throw new Error(`SUPERVISED_AGENCY_ROW_INVALID: group ${groupId} carries an agency without a positive id and a name`)
        }
        // EXACT BINDING. The agency's own `groupId` must equal its containing group. Neither the
        // array position, nor the display label, nor any client state decides the owning group.
        if (agencyGroupId !== groupId) {
          throw new Error(`SUPERVISED_AGENCY_GROUP_BINDING_MISMATCH: agency ${agencyId} declares groupId ${agencyGroupId} inside group ${groupId}`)
        }
        agencyOptions.push({
          agencyId,
          agencyName,
          address: typeof agency.address === 'string' && agency.address.trim() ? agency.address : null,
          groupId: agencyGroupId,
          groupName
        })
      }
    }
    return { groups: groupOptions, agencies: agencyOptions }
  }
  
  /**
   * Obtiene las incidencias del supervisor filtradas por sus grupos
   * @param supervisorId ID del supervisor
   * @returns Promise con las incidencias d  /**
   * Obtiene las incidencias activas creadas por el supervisor
   * @param supervisorId ID del supervisor
   * @returns Promise con las incidencias activas
   */
  static async getMyActiveIncidents(supervisorId: number): Promise<Incident[]> {
    try {
      // Usar el endpoint que sí funciona y filtrar por activas
      console.log('🔧 Usando endpoint existente para mis incidencias activas')
      const response = await ApiClient.get('/field-supervisor/incidents')
      let allIncidents = response.data?.incidents || []
      
      console.log('🔍 getMyActiveIncidents: Datos recibidos:', {
        hasData: !!response.data,
        incidentsLength: allIncidents.length,
        firstIncident: allIncidents[0]
      })
      
      // NO usar fallback - devolver vacío si no hay datos reales
      if (!allIncidents.length) {
        console.log('ℹ️ No hay incidencias activas del backend')
        return []
      }
      
      // Filtrar solo las activas usando los valores reales del backend
      // P13-V1D: predicado canónico (trabajo abierto = pending + in_progress + pending_validation).
      const myActiveIncidents = allIncidents.filter((inc: any) =>
        ['pending', 'in_progress', 'pending_validation'].includes(String(inc.status || '').toLowerCase())
      )
      
      console.log('✅ getMyActiveIncidents: Incidencias activas filtradas:', {
        total: allIncidents.length,
        filtered: myActiveIncidents.length,
        incidents: myActiveIncidents
      })
      
      return myActiveIncidents
    } catch (error) {
      console.error('Error fetching my active incidents:', error)
      throwIfFieldSupervisorAuthorityError(error)
      // NO usar fallback - devolver vacío en caso de error
      return []
    }
  }

  /**
   * Obtiene todas las incidencias del supervisor
   * @param supervisorId ID del supervisor
   * @returns Promise con las incidencias del supervisor
   */
  static async getSupervisorIncidents(supervisorId: number): Promise<Incident[]> {
    try {
      // Usar el endpoint real que existe en el backend
      const response = await ApiClient.get(`/field-supervisor/incidents`)
      return response.data?.incidents || []
    } catch (error) {
      console.error('Error fetching supervisor incidents:', error)
      throwIfFieldSupervisorAuthorityError(error)
      // NO usar fallback - devolver vacío en caso de error
      return []
    }
  }

  /**
   * Obtiene las rutas asignadas al supervisor
   * @param supervisorId ID del supervisor
   * @returns Promise con las rutas del supervisor
   */
  static async getSupervisorRoutes(supervisorId: number): Promise<Route[]> {
    try {
      // TODO: Implementar endpoint real cuando esté disponible
      console.log('⚠️ Endpoint de rutas no implementado - devolviendo vacío')
      return []
    } catch (error) {
      console.error('Error fetching supervisor routes:', error)
      return []
    }
  }

  /**
   * Obtiene los puntos de venta asignados al supervisor
   * @param supervisorId ID del supervisor
   * @returns Promise con los puntos de venta del supervisor
   */
  static async getSupervisorPOSPoints(supervisorId: number): Promise<any[]> {
    try {
      console.log('📍 Cargando agencias asignadas al supervisor desde backend')
      const response = await ApiClient.get('/field-supervisor/assigned-agencies')
      const agencies = response.data?.data?.agencies || response.data?.agencies || []
      
      console.log(`✅ Agencias cargadas para mapa: ${agencies.length}`)
      
      // Filtrar solo agencias con coordenadas válidas
      const agenciesWithCoords = agencies.filter((agency: any) => 
        agency.coordinates?.lat && agency.coordinates?.lng
      )
      
      console.log(`📍 Agencias con coordenadas válidas: ${agenciesWithCoords.length}`)
      
      // Transformar al formato esperado por el mapa
      return agenciesWithCoords.map((agency: any) => ({
        id: agency.id,
        name: agency.name,
        address: agency.address,
        latitude: agency.coordinates.lat,
        longitude: agency.coordinates.lng,
        status: agency.status,
        priority_level: agency.priority_level || 'normal',
        pending_incidents: agency.pending_incidents || 0,
        group: agency.group
      }))
    } catch (error) {
      console.error('❌ Error fetching supervisor POS points:', error)
      throwIfFieldSupervisorAuthorityError(error)
      return []
    }
  }

  // P13-V7B: `getSupervisorSummary` and `getSupervisorStats` were REMOVED. They read
  // `GET /supervisor/:id/summary` and `GET /supervisor/:id/stats`; the backend retired the whole
  // `/api/supervisor` family (every path answers 410) and neither method had a caller.

  /**
   * Obtiene las incidencias activas en los grupos del supervisor
   * @param supervisorId ID del supervisor
   * @returns Promise con las incidencias activas
   */
  static async getActiveSupervisorIncidents(supervisorId: number): Promise<Incident[]> {
    try {
      // Usar el endpoint que sí funciona y filtrar por activas
      console.log('🔧 Usando endpoint existente para incidencias activas')
      const response = await ApiClient.get('/field-supervisor/incidents')
      const allIncidents = response.data?.incidents || []
      
      // Filtrar solo las activas (pending e in_progress)
      const activeIncidents = allIncidents.filter((inc: any) => 
        inc.status === 'pending' || inc.status === 'in_progress'
      )
      
      return activeIncidents
    } catch (error) {
      console.error('Error fetching active supervisor incidents:', error)
      throwIfFieldSupervisorAuthorityError(error)
      return []
    }
  }

  /**
   * Obtiene las incidencias reportadas por empleados en los grupos del supervisor
   * @param supervisorId ID del supervisor
   * @returns Promise con las incidencias reportadas
   */
  static async getEmployeeReportedIncidents(supervisorId: number): Promise<Incident[]> {
    try {
      // Usar el endpoint que sí funciona y filtrar por empleados
      console.log('🔧 Usando endpoint existente para incidencias de empleados')
      const response = await ApiClient.get('/field-supervisor/incidents')
      const allIncidents = response.data?.incidents || []
      
      // Filtrar incidencias reportadas por otros (no por el supervisor)
      const employeeIncidents = allIncidents.filter((inc: any) => 
        inc.reportedBy && inc.reportedBy !== supervisorId
      )
      
      return employeeIncidents
    } catch (error) {
      console.error('Error fetching employee reported incidents:', error)
      throwIfFieldSupervisorAuthorityError(error)
      return []
    }
  }

  /**
   * Obtiene los grupos asignados a un supervisor
   * @param supervisorId ID del supervisor
   * @returns Promise con los grupos asignados
   */
  static async getSupervisorGroups(supervisorId: number) {
    try {
      // Usar agencias asignadas y derivar grupos
      const resp = await ApiClient.get('/field-supervisor/assigned-agencies')
      const list = resp.data?.data?.agencies || resp.data?.agencies || []
      const map = new Map<number, string>()
      for (const a of list) {
        const gId = a.group?.id || a.groupId
        const gName = a.group?.name || `Grupo ${gId}`
        if (gId) map.set(gId, gName)
      }
      return Array.from(map.entries()).map(([id, name]) => ({ id, name }))
    } catch (error) {
      console.error('Error fetching supervisor groups:', error)
      throwIfFieldSupervisorAuthorityError(error)
      return []
    }
  }

  // ================== MÓDULO 4.1.3: GESTIÓN DE INCIDENCIAS PROPIAS ==================

  /**
   * Obtiene el historial de incidencias creadas por el supervisor
   * @param supervisorId ID del supervisor
   * @param filters Filtros opcionales (estado, fecha, prioridad)
   * @returns Promise con el historial de incidencias
   */
  static async getMyIncidentsHistory(supervisorId: number, filters?: {
    status?: string
    dateFrom?: string
    dateTo?: string
    priority?: string
  }): Promise<Incident[]> {
    try {
      // Usar el endpoint que sí funciona: /field-supervisor/incidents
      console.log('🔧 Usando endpoint existente de Field Supervisor para historial de incidencias')
      const response = await ApiClient.get('/field-supervisor/incidents')
      
      // Si hay filtros, aplicarlos en el frontend por ahora
      let incidents = response.data?.incidents || []
      
      console.log('📊 getMyIncidentsHistory: Incidencias del backend:', incidents.length)
      
      if (filters) {
        if (filters.status) {
          incidents = incidents.filter((inc: any) => inc.status === filters.status)
        }
        if (filters.priority) {
          incidents = incidents.filter((inc: any) => inc.priority === filters.priority)
        }
        if (filters.dateFrom || filters.dateTo) {
          incidents = incidents.filter((inc: any) => {
            const incDate = new Date(inc.createdAt || inc.created_at)
            if (filters.dateFrom && incDate < new Date(filters.dateFrom)) return false
            if (filters.dateTo && incDate > new Date(filters.dateTo)) return false
            return true
          })
        }
      }
      
      return incidents
    } catch (error) {
      console.error('Error fetching my incidents history:', error)
      throwIfFieldSupervisorAuthorityError(error)
      return []
    }
  }

  /**
   * Obtiene las incidencias reportadas por empleados bajo supervisión
   * @param supervisorId ID del supervisor
   * @param status Filtro por estado opcional
   * @returns Promise con las incidencias reportadas por empleados
   */
  static async getEmployeeIncidents(supervisorId: number, status?: string): Promise<Incident[]> {
    try {
      console.log('🔧 Usando endpoint existente para incidencias de empleados')
      const response = await ApiClient.get('/field-supervisor/incidents')
      let allIncidents = response.data?.incidents || []
      
      console.log('🔍 getEmployeeIncidents: Datos recibidos:', allIncidents.length)
      
      if (!allIncidents.length) {
        console.log('ℹ️ No hay incidencias en el backend')
        return []
      }
      
      // Filtrar incidencias reportadas por empleados (no por el supervisor)
      let employeeIncidents = allIncidents.filter((inc: any) => 
        inc.reportedBy && inc.reportedBy !== supervisorId
      )
      
      // Aplicar filtro de estado si se proporciona
      if (status) {
        employeeIncidents = employeeIncidents.filter((inc: any) => inc.status === status)
      }
      
      console.log('✅ getEmployeeIncidents procesadas:', employeeIncidents.length)
      return employeeIncidents
    } catch (error) {
      console.error('Error fetching employee incidents:', error)
      throwIfFieldSupervisorAuthorityError(error)
      return []
    }
  }

  /**
   * Crea una nueva incidencia como supervisor
   * @param supervisorId ID del supervisor
   * @param incidentData Datos de la nueva incidencia
   * @returns Promise con la incidencia creada
   */
  static async createIncident(supervisorId: number, incidentData: {
    title: string
    description: string
    priority: 'alta' | 'media' | 'baja'
    category: string
    location: {
      lat: number
      lng: number
      address?: string
    }
    assignedTo?: number
    groupId: number
  }): Promise<Incident> {
    const error = new Error('La creación de incidencias desde este servicio está pendiente de un contrato backend scoped.')
    ;(error as any).status = 501
    ;(error as any).code = 'NOT_IMPLEMENTED_YET'
    throw error
  }

  /**
   * P13-V7B (refutation) — the zone counts of `/supervisor/incidents`, published only when the rows
   * the server returned ARE the whole set.
   *
   * `GET /field-supervisor/incidents` returns the incidences of every group this supervisor
   * canonically supervises (not the ones the supervisor reported), newest first, as ONE page:
   * `?limit` is clamped server-side to 1..200 (default 50) and the response states no total — its
   * `summary.total` is the length of that same page. The old reader published the page length as
   * "Mis Incidencias", so a zone with 180 incidences read "50", and a failure read "0".
   *
   * The rule now: ask for the largest page the server serves; when fewer rows than that come back,
   * the page IS the zone and every count below is exact. When the page is full, the server has not
   * said how many exist, so no count is published (`null`) and `complete` says why. A shape that is
   * not a list is a named failure, never an empty zone.
   */
  static async getIncidentStats(supervisorId: number): Promise<FieldSupervisorZoneIncidentStats> {
    try {
      const response = await ApiClient.get('/field-supervisor/incidents', {
        params: { limit: FS_ZONE_INCIDENTS_PAGE_LIMIT }
      })
      const incidents = response.data?.incidents
      if (!Array.isArray(incidents)) {
        throw new Error('FS_ZONE_INCIDENTS_SHAPE_INVALID: /field-supervisor/incidents did not return an incidents list')
      }

      const complete = incidents.length < FS_ZONE_INCIDENTS_PAGE_LIMIT
      const status = (inc: any) => String(inc?.status || '').toLowerCase()
      const count = (predicate: (inc: any) => boolean): number | null =>
        complete ? incidents.filter(predicate).length : null

      return {
        complete,
        pageLimit: FS_ZONE_INCIDENTS_PAGE_LIMIT,
        zoneIncidents: complete ? incidents.length : null,
        // P13-V1D: trabajo abierto = pending + in_progress + pending_validation (vocabulario canónico).
        openIncidents: count((inc) => ['pending', 'in_progress', 'pending_validation'].includes(status(inc))),
        closedIncidents: count((inc) => status(inc) === 'closed'),
        // The row's own `reportedBy`, compared with the session's user id — nothing more is claimed.
        reportedByOthers: count((inc) => Boolean(inc?.reportedBy) && inc.reportedBy !== supervisorId)
      }
    } catch (error) {
      console.error('Error fetching incident stats:', error)
      throwIfFieldSupervisorAuthorityError(error)
      throw error
    }
  }

  /**
   * Obtiene los detalles de una incidencia específica (solo si tiene acceso)
   * @param supervisorId ID del supervisor
   * @param incidentId ID de la incidencia
   * @returns Promise con los detalles de la incidencia
   */
  static async getIncidentDetails(supervisorId: number, incidentId: number): Promise<Incident | null> {
    try {
      console.log('🔧 Buscando incidencia específica en endpoint existente')
      const response = await ApiClient.get('/field-supervisor/incidents')
      const allIncidents = response.data?.incidents || []
      
      const incident = allIncidents.find((inc: any) => inc.id === incidentId)
      
      if (incident) {
        return incident
      } else {
        console.warn(`Incidencia ${incidentId} no encontrada`)
        return null
      }
    } catch (error) {
      console.error('Error fetching incident details:', error)
      throwIfFieldSupervisorAuthorityError(error)
      return null
    }
  }

  /**
   * P13-V6B@V1 (FS_COMMENTS_DECISION = RETIRE_WITH_CALLER_MIGRATION) — los comentarios usan la
   * ÚNICA superficie canónica `/incidences/:incidentId/comments*`. Los endpoints dedicados
   * `/field-supervisor/comments*` fueron retirados (404 natural). El delete adopta la semántica
   * canónica (autor o super_admin; texto preservado con placeholder) — convergencia intencional.
   *
   * Añade un comentario a una incidencia (create + reply vía replyToId).
   * @param incidentId ID de la incidencia (requerido por la ruta canónica)
   * @param content Texto del comentario
   * @param replyToId ID del comentario al que se responde (opcional)
   */
  static async addIncidentComment(incidentId: number, content: string, replyToId?: number) {
    try {
      const response = await ApiClient.post(`/incidences/${incidentId}/comments`, {
        comment: content,
        replyToId: replyToId ?? null
      })

      console.log('✅ Comentario añadido:', response.data)

      return {
        success: true,
        comment: response.data.data
      }
    } catch (error) {
      console.error('❌ Error añadiendo comentario:', error)
      throw error
    }
  }

  /**
   * Edita un comentario existente (ruta canónica; requiere el incidentId del padre).
   */
  static async editIncidentComment(incidentId: number, commentId: number, content: string) {
    try {
      const response = await ApiClient.put(`/incidences/${incidentId}/comments/${commentId}`, {
        comment: content
      })

      console.log('✅ Comentario editado:', response.data)

      return {
        success: true,
        comment: response.data.data
      }
    } catch (error) {
      console.error('❌ Error editando comentario:', error)
      throw error
    }
  }

  /**
   * Elimina un comentario (ruta canónica: autor o super_admin; retención preserve server-side).
   */
  static async deleteIncidentComment(incidentId: number, commentId: number) {
    try {
      const response = await ApiClient.delete(`/incidences/${incidentId}/comments/${commentId}`)

      console.log('✅ Comentario eliminado:', response.data)

      return {
        success: true,
        message: response.data.message
      }
    } catch (error) {
      console.error('❌ Error eliminando comentario:', error)
      throw error
    }
  }
  /**
   * Obtiene las rutas asignadas al supervisor
   * @param supervisorId ID del supervisor
   * @returns Promise con las rutas asignadas
   */
  static async getAssignedRoutes(supervisorId: number): Promise<{
    active: Array<{
      id: number
      name: string
      progress: number
      status: string
      operatorId?: number
      operatorName?: string
    }>
    completed: Array<{
      id: number
      name: string
      completedAt: string
      operatorId: number
      operatorName: string
    }>
    total: number
  }> {
    try {
      // Usar el endpoint que funciona
      console.log('🔧 Obteniendo rutas asignadas desde endpoint Field Supervisor')
      const response = await ApiClient.get('/field-supervisor/dashboard')
      
      // Extraer datos de rutas si están disponibles
      const routesData = response.data?.routes || {}
      
      return {
        active: routesData.active || [],
        completed: routesData.completed || [],
        total: (routesData.active?.length || 0) + (routesData.completed?.length || 0)
      }
    } catch (error) {
      console.error('Error fetching assigned routes:', error)
      throwIfFieldSupervisorAuthorityError(error)
      return { active: [], completed: [], total: 0 }
    }
  }
}

export default SupervisorService
